/**
 * !!!
 * This component is inherited from Dewo SDK, and needs a refactor to be more generic and reusable.
 *
 * @todo
 * - [ ] Upgrade this to latest standards
 * - [x] Remove Dewo SDK's Form component/replace usage with this.
 */

import classNames from 'classnames';
import React from 'react';
import type { GenericUIProps, NameSizingScale, TransformedCSSProperties } from '../component.types';
import { Text } from '../text/Text';
import { transformSpecialCSSProperties } from '../utils';
import styles from './Form.module.scss';

// TODO: Move frontend type utilities to package for re-use
// ref timely-front/src/types/utility.ts
type Merge<M, N> = Omit<M, Extract<keyof M, keyof N>> & N;

interface ContainerProps extends Omit<GenericUIProps, 'as'>, TransformedCSSProperties {}

const Container = React.forwardRef<HTMLFormElement, ContainerProps>((props, ref) => {
  const { className, ...finalProps } = transformSpecialCSSProperties(props);
  return <form {...finalProps} className={classNames(styles.container, className)} ref={ref} />;
});

interface CommonFormProps
  extends Omit<GenericUIProps, 'as' | 'children'>,
    TransformedCSSProperties {
  size?: NameSizingScale | 'xsmall';
}

interface InputProps extends Merge<React.HTMLProps<HTMLInputElement>, CommonFormProps> {
  invalid?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    className,
    size = 'medium',
    type = 'text',
    invalid,
    ...finalProps
  } = transformSpecialCSSProperties(props);
  return (
    <input
      autoCorrect="off"
      autoCapitalize="none"
      {...finalProps}
      type={type}
      className={classNames(
        styles.input,
        styles[`size-${size}`],
        { [styles.invalid]: invalid },
        className
      )}
      ref={ref}
    />
  );
});

const Radio = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  return <Input {...props} type="radio" ref={ref} />;
});

const Checkbox = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  return <Input {...props} type="checkbox" ref={ref} />;
});

type SelectProps = Merge<React.HTMLProps<HTMLSelectElement>, CommonFormProps>;

const Select = React.forwardRef<HTMLSelectElement, SelectProps>((props, ref) => {
  const { className, size = 'medium', ...finalProps } = transformSpecialCSSProperties(props);
  return (
    <select
      {...finalProps}
      className={classNames(styles.input, styles[`size-${size}`], className)}
      ref={ref}
    />
  );
});

type LabelProps = Merge<Omit<React.HTMLProps<HTMLLabelElement>, 'color'>, CommonFormProps>;

const Label = React.forwardRef<HTMLLabelElement, LabelProps>((props, ref) => {
  return (
    <Text.Label
      // @ts-expect-error Not super sure why, but don't feel like chasing this rabbit hole today
      color={props.disabled ? ('lowContrast' as const) : undefined}
      {...props}
      className={classNames(styles.label, props.className)}
      ref={ref}
    >
      {props.children}
    </Text.Label>
  );
});

type TextareaProps = Merge<React.HTMLProps<HTMLTextAreaElement>, CommonFormProps>;

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>((props, ref) => {
  const { className, size = 'medium', ...finalProps } = transformSpecialCSSProperties(props);
  return (
    <textarea
      {...finalProps}
      className={classNames(styles.input, styles[`size-${size}`], className)}
      ref={ref}
    />
  );
});

type ErrorProps = Omit<Merge<React.HTMLProps<HTMLElement>, CommonFormProps>, 'color'> & {
  errorMessage?: string;
};

const FormError = React.forwardRef<HTMLElement, ErrorProps>((props, ref) => {
  if (!props.children && !props.errorMessage) return null;

  return (
    <Text.Label
      as="div"
      size="xsmall"
      {...props}
      className={classNames(styles.error, props.className)}
      ref={ref}
    >
      {props.errorMessage || props.children}
    </Text.Label>
  );
});

/** <form> element */
export const Form = Object.assign(Container, {
  /** Text input field; See more specific implementations for other elements */
  Input,
  /** A form label, pairs nicely with `Input` */
  Label,
  /** Simple radio */
  Radio,
  /** Simple checkbox */
  Checkbox,
  /** Simple select */
  Select,
  /** Simple textarea */
  Textarea,
  /** Feed it error messages from Zod/react-form-hook as string. Doesn't render unless it's got errors */
  Error: FormError,
});
